/*
 * Gift bag (replaces the Wrapin app).
 * - Product page: shows the bag that fits the selected kit size, and adds the same quantity
 *   of bags right before the kit goes into the cart.
 * - Cart: a bag stays only while the cart has a kit that uses that bag, so a bag-only
 *   cart is cleared automatically. Customers may raise the bag quantity freely.
 * Config comes from snippets/py-gift-bag-config.liquid (window.pyGiftBag).
 */
(() => {
  const config = window.pyGiftBag;
  if (!config || window.pyGiftBagLoaded) return;
  window.pyGiftBagLoaded = true;

  const normalize = (text) =>
    String(text || '')
      .toLowerCase()
      .replace(/[×ｘ✕]/g, 'x')
      .replace(/cm|\s/g, '');
  const tokens = String(config.smallMatch || '')
    .split(',')
    .map(normalize)
    .filter(Boolean);

  const isKitSku = (sku) => config.kitSkuPrefixes.some((prefix) => String(sku || '').startsWith(prefix));
  // Kits whose size text or SKU segment (C-4032-24 -> 4032) matches a token use the small bag.
  const bagKindFor = ({ title, sku }) => {
    const name = normalize(title);
    const segments = normalize(sku).split('-');
    const small = tokens.some((token) => name.includes(token) || segments.includes(token));
    return small ? 'small' : 'default';
  };
  const bagIds = (kind) => (config.bags[kind] || []).map(Number);
  const kindOfBagVariant = (variantId) =>
    ['default', 'small'].find((kind) => bagIds(kind).includes(Number(variantId)));

  const sectionsToUpdate = () => {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
    );
    return sections;
  };

  /* ---------- Cart rules ---------- */

  let enforcing = false;
  const enforceCartRules = async () => {
    if (enforcing) return;
    enforcing = true;
    try {
      const cart = await (await fetch(`${FoxTheme.routes.cart_url}.js`)).json();
      const allowed = { default: 0, small: 0 };
      cart.items.forEach((item) => {
        if (isKitSku(item.sku)) allowed[bagKindFor({ title: item.variant_title, sku: item.sku })] += item.quantity;
      });

      const updates = {};
      cart.items.forEach((item) => {
        const kind = kindOfBagVariant(item.variant_id);
        if (kind && !allowed[kind]) updates[item.key] = 0;
      });
      if (!Object.keys(updates).length) return;

      const sections = sectionsToUpdate();
      const response = await fetch(`${FoxTheme.routes.cart_update_url}`, {
        ...FoxTheme.utils.fetchConfig(),
        body: JSON.stringify({ updates, sections }),
      });
      const state = await response.json();
      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: state });
    } catch (error) {
      console.error('py-gift-bag: cart rules', error);
    } finally {
      enforcing = false;
    }
  };

  /* ---------- Product page ---------- */

  const setupProductPage = (root) => {
    const form =
      document.querySelector('.product__block--buy_buttons form[is="product-form"]') ||
      document.querySelector('form[is="product-form"]');
    const variants = JSON.parse(root.querySelector('[data-gift-bag-variants]').textContent);
    const options = [...root.querySelectorAll('.py-gift-bag__option')];
    const error = root.querySelector('.py-gift-bag__error');
    let current = null;

    const currentVariantId = () => Number(form?.querySelector('[name="id"]')?.value);

    const show = (variantId) => {
      const variant = variants.find((v) => v.id === Number(variantId)) || variants[0];
      const kind = variant && isKitSku(variant.sku) && bagKindFor(variant);
      const wasChecked = current?.querySelector('[data-gift-bag-check]').checked;
      current = options.find((option) => option.dataset.bagKind === kind) || null;
      options.forEach((option) => {
        const active = option === current;
        const available = option.dataset.available === 'true';
        const check = option.querySelector('[data-gift-bag-check]');
        option.hidden = !active;
        check.disabled = !available;
        option.querySelector('.py-gift-bag__soldout').hidden = available;
        if (!active || !available) check.checked = false;
        else if (wasChecked) check.checked = true;
      });
      root.hidden = !current;
    };

    show(currentVariantId());
    document.addEventListener('variant:changed', (event) => show(event.detail?.variant?.id || currentVariantId()));

    // The bag goes into the cart just before the kit, so the kit is listed first (the cart shows
    // the newest line on top) and the drawer opens with both. A bag left alone because the kit
    // failed is removed by the cart rules.
    const addBagFor = async (kitVariantId, quantity) => {
      const check = current?.querySelector('[data-gift-bag-check]');
      const kit = variants.find((v) => v.id === Number(kitVariantId));
      if (!check?.checked || check.disabled || !kit || !isKitSku(kit.sku)) return;
      error.hidden = true;
      try {
        const response = await fetch(`${FoxTheme.routes.cart_add_url}.js`, {
          ...FoxTheme.utils.fetchConfig(),
          body: JSON.stringify({
            // No per-kit property, so bags of the same size merge into one line (L x2 on the order sheet).
            items: [{ id: Number(current.dataset.variantId), quantity }],
          }),
        });
        if (!response.ok) throw new Error(`cart add ${response.status}`);
      } catch (e) {
        console.error('py-gift-bag: add', e);
        error.hidden = false;
      }
    };

    const submitKit = form?.handleFormSubmission;
    if (typeof submitKit === 'function') {
      form.handleFormSubmission = async (fetchConfig) => {
        const body = fetchConfig.body;
        const quantity = Math.max(1, parseInt(body?.get?.('quantity'), 10) || 1);
        await addBagFor(body?.get?.('id'), quantity);
        submitKit(fetchConfig);
      };
      document.addEventListener('product-ajax:error', () => enforceCartRules());
    } else {
      // Fallback if the theme's product form changes: add the bag after the kit (bag listed above it).
      document.addEventListener('product-ajax:added', async (event) => {
        const added = event.detail?.product || {};
        await addBagFor(added.variant_id || added.id, 1);
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
      });
    }
  };

  const init = () => {
    document.querySelectorAll('[data-py-gift-bag]').forEach(setupProductPage);
    enforceCartRules();
    FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, () => setTimeout(enforceCartRules, 300));
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
